import { ClareServicePlan, ClareServicePlanData, ClareServicePlanHelper } from "@app/project/model/clare-service-plan";
import { Observable, catchError, map, tap } from "rxjs";

import { Injectable } from "@angular/core";
import { ClareDbCollection } from "@app/shared/resources/resource-objectifier";
import { ClareWSCrudHelperService } from "@app/shared/services/clare-ws-crud-helper.service";
import { DataServiceError } from "@app/shared/services/data-service-error";
import { DbCrudHelper } from "@app/shared/services/db-crud-helper";
import { TSLoggingService } from "@app/shared/services/ts-logging.service";

// Number of accounts enrolled in a service plan
interface PlanSummary {
    display_json: { color: string };
    name: string;
    service_plan_count: number;
    service_plan_id: number;
}

// Accounts without a service plan are part of this category
interface NoPlan {
    display_json: null;
    name: null;
    service_plan_id: null;
    service_plan_count: number;
}

export type ServicePlanSummary = PlanSummary | NoPlan;

@Injectable()
export class ServicePlansDbService {
    servicePlanFeatureTable = `service_plan_feature`;
    priceCol = `price`;
    relationalTable = `service_plan_feature_price`;
    entityServicePlansEndpoint = `rpc/entity_service_plans`;
    private helper = new ClareServicePlanHelper();
    crudHelperService: ClareWSCrudHelperService<ClareServicePlan, ClareServicePlanData>;

    /**
     * @description Fetches the service plans and their optional and standard features from the database
     */
    getAllServicePlans(): Observable<ClareDbCollection<ClareServicePlan>> {
        return this.crudHelperService
            .retrieve({
                endpoint: "GetAllServicePlans",
            })
            .pipe(
                map(planData =>
                    planData.reduce((collection, d) => {
                        const plan = this.helper.fromDbObject(d);
                        return {
                            ...collection,
                            [plan.id]: plan,
                        };
                    }, {})
                ),
                catchError(DataServiceError.handleError())
            );
    }

    /**
     * @description Fetches the service plan summary which is a database function that retrieves the subscriptions
     * for a given entity and sums up the number of accounts assigned to each type of service plan. This is used
     * for the dashbaord view.
     * @param entityId
     * @returns summary of accounts enrolled in each plan, null if the entityId is invalid
     */
    getServicePlansSummary(entityId: number): Observable<ServicePlanSummary[]> {
        return this.crudHelper.makeRequest<ServicePlanSummary[] | null>("GetEntityServicePlans", { id: entityId }).pipe(
            tap(result => {
                if (result === null) {
                    console.error(`No company exists with id ${entityId}`);
                    throw new DataServiceError(`Invalid entityId=${entityId}`);
                }
            })
        );
    }

    constructor(tsLoggingService: TSLoggingService, protected crudHelper: DbCrudHelper) {
        this.crudHelperService = new ClareWSCrudHelperService(tsLoggingService, crudHelper, this.helper);
    }
}
